import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { Target, Plus, Trash2, CheckCircle, X, Clock, Eye, EyeOff, Trophy, ChevronDown, ChevronUp } from '../../components/icons';
import {
  fetchGoals, createGoal, updateGoal, deleteGoal, toggleGoalSharedWithCoach,
  fetchMilestones, createMilestone, deleteMilestone,
  type Goal, type Milestone,
} from '../../lib/progress';
import { LoadingSpinner } from '../../components/LoadingSpinner';

const STATUS_LABELS: Record<Goal['status'], string> = {
  active: 'Active',
  completed: 'Completed',
  paused: 'Paused',
  abandoned: 'Abandoned',
};

const STATUS_STYLES: Record<Goal['status'], string> = {
  active: 'bg-sage-100 text-sage-700',
  completed: 'bg-blue-100 text-blue-700',
  paused: 'bg-amber-100 text-amber-700',
  abandoned: 'bg-red-100 text-red-700',
};

const STATUS_CYCLE: Goal['status'][] = ['active', 'completed', 'paused', 'abandoned'];

interface GoalRowProps {
  goal: Goal;
  milestones: Milestone[];
  onStatusCycle: (goal: Goal) => void;
  onEdit: (goal: Goal) => void;
  onDelete: (id: string) => void;
  onToggleShare: (goal: Goal) => void;
  onAddMilestone: (goal: Goal) => void;
  onDeleteMilestone: (id: string) => void;
  milestoneFormGoalId: string | null;
  milestoneFormTitle: string;
  onMilestoneTitleChange: (value: string) => void;
  onMilestoneSubmit: (e: React.FormEvent, goal: Goal) => void;
  onMilestoneCancel: () => void;
}

function GoalRow({
  goal, milestones, onStatusCycle, onEdit, onDelete, onToggleShare,
  onAddMilestone, onDeleteMilestone,
  milestoneFormGoalId, milestoneFormTitle, onMilestoneTitleChange,
  onMilestoneSubmit, onMilestoneCancel,
}: GoalRowProps) {
  const goalMilestones = milestones.filter(m => m.goal_id === goal.id);
  const isAddingMilestone = milestoneFormGoalId === goal.id;

  return (
    <div className="freud-card-elevated p-4">
      <div className="flex items-start gap-4">
        <button onClick={() => onStatusCycle(goal)} className="mt-0.5 flex-shrink-0" title="Click to cycle status">
          {goal.status === 'completed'
            ? <CheckCircle className="w-5 h-5 text-sage-500" />
            : goal.status === 'paused'
            ? <Clock className="w-5 h-5 text-amber-500" />
            : goal.status === 'abandoned'
            ? <X className="w-5 h-5 text-red-400" />
            : <CheckCircle className="w-5 h-5 text-chocolate-200" />}
        </button>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${STATUS_STYLES[goal.status]}`}>{STATUS_LABELS[goal.status]}</span>
            {goal.target_date && (
              <span className="text-[11px] text-chocolate-400">by {new Date(goal.target_date).toLocaleDateString()}</span>
            )}
            {goalMilestones.length > 0 && (
              <span className="inline-flex items-center gap-1 text-[11px] font-medium text-yellow-600 bg-yellow-50 px-1.5 py-0.5 rounded">
                <Trophy className="w-3 h-3" />
                {goalMilestones.length} {goalMilestones.length === 1 ? 'milestone' : 'milestones'}
              </span>
            )}
          </div>
          <p className={`mt-1 font-medium text-chocolate-900 ${goal.status === 'abandoned' ? 'line-through text-chocolate-400' : ''}`}>{goal.title}</p>
          {goal.description && <p className="text-sm text-chocolate-500 mt-0.5">{goal.description}</p>}

          {/* Inline milestones */}
          {goalMilestones.length > 0 && (
            <div className="mt-3 space-y-1.5 pl-2 border-l-2 border-yellow-200">
              {goalMilestones.map(m => (
                <div key={m.id} className="flex items-center gap-2 group">
                  <Trophy className="w-3.5 h-3.5 text-yellow-500 flex-shrink-0" />
                  <span className="text-sm text-chocolate-600 flex-1 truncate">{m.title}</span>
                  <span className="text-[10px] text-chocolate-400 flex-shrink-0">
                    {new Date(m.achieved_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </span>
                  <button
                    onClick={() => onDeleteMilestone(m.id)}
                    className="p-0.5 rounded text-chocolate-300 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0"
                  >
                    <Trash2 className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          {/* Inline add milestone form */}
          {isAddingMilestone && (
            <form onSubmit={(e) => onMilestoneSubmit(e, goal)} className="mt-3 flex gap-2">
              <input
                className="freud-input flex-1 text-sm py-1.5"
                value={milestoneFormTitle}
                onChange={e => onMilestoneTitleChange(e.target.value)}
                placeholder="Milestone title..."
                autoFocus
              />
              <button type="submit" className="freud-btn-primary text-sm py-1.5 px-3">Add</button>
              <button type="button" onClick={onMilestoneCancel} className="freud-btn-secondary text-sm py-1.5 px-3">Cancel</button>
            </form>
          )}

          {/* Add milestone button */}
          {!isAddingMilestone && goal.status !== 'abandoned' && (
            <button
              onClick={() => onAddMilestone(goal)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-yellow-600 hover:text-yellow-700 transition-colors"
            >
              <Plus className="w-3.5 h-3.5" />
              Add milestone
            </button>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => onToggleShare(goal)}
            title={goal.shared_with_coach ? 'Shared with coach' : 'Private — click to share with coach'}
            className={`p-1.5 rounded-lg transition-colors ${goal.shared_with_coach ? 'text-sage-500 hover:bg-sage-50' : 'text-chocolate-300 hover:bg-cream-100'}`}
          >
            {goal.shared_with_coach ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
          </button>
          <button onClick={() => onEdit(goal)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-cream-100 hover:text-chocolate-700 transition-colors">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" /></svg>
          </button>
          <button onClick={() => onDelete(goal.id)} className="p-1.5 rounded-lg text-chocolate-400 hover:bg-red-50 hover:text-red-500 transition-colors">
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface CollapsibleSectionProps {
  title: string;
  count: number;
  icon: React.ComponentType<{ className?: string }>;
  iconBg: string;
  iconColor: string;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

function CollapsibleSection({ title, count, icon: Icon, iconBg, iconColor, children, defaultOpen = true }: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  return (
    <div>
      <button
        onClick={() => setIsOpen(v => !v)}
        className="flex items-center gap-3 mb-3 w-full text-left group"
      >
        <div className={`w-7 h-7 ${iconBg} rounded-lg flex items-center justify-center flex-shrink-0`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <span className="text-sm font-bold text-chocolate-800">{title}</span>
        <span className="text-xs text-chocolate-400">({count})</span>
        <span className="ml-auto text-chocolate-400 group-hover:text-chocolate-600 transition-colors">
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </span>
      </button>
      {isOpen && <div className="space-y-3">{children}</div>}
    </div>
  );
}

export function GoalsView() {
  const { user } = useAuth();
  const [goals, setGoals] = useState<Goal[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingGoal, setEditingGoal] = useState<Goal | null>(null);
  const [form, setForm] = useState({ title: '', description: '', target_date: '', status: 'active' as Goal['status'] });
  const [milestoneFormGoalId, setMilestoneFormGoalId] = useState<string | null>(null);
  const [milestoneFormTitle, setMilestoneFormTitle] = useState('');

  useEffect(() => {
    if (!user) return;
    Promise.all([
      fetchGoals(user.id),
      fetchMilestones(user.id),
    ]).then(([g, m]) => {
      setGoals(g);
      setMilestones(m);
    }).finally(() => setIsLoading(false));
  }, [user]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!user || !form.title.trim()) return;
    if (editingGoal) {
      await updateGoal(editingGoal.id, form);
      setGoals(prev => prev.map(g => g.id === editingGoal.id ? { ...g, ...form } : g));
    } else {
      const created = await createGoal(user.id, form);
      if (created) setGoals(prev => [created, ...prev]);
    }
    setShowForm(false);
    setEditingGoal(null);
    setForm({ title: '', description: '', target_date: '', status: 'active' });
  };

  const handleEdit = (goal: Goal) => {
    setEditingGoal(goal);
    setForm({ title: goal.title, description: goal.description || '', target_date: goal.target_date || '', status: goal.status });
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    await deleteGoal(id);
    setGoals(prev => prev.filter(g => g.id !== id));
  };

  const handleStatusCycle = async (goal: Goal) => {
    const next = STATUS_CYCLE[(STATUS_CYCLE.indexOf(goal.status) + 1) % STATUS_CYCLE.length];
    await updateGoal(goal.id, { status: next });
    setGoals(prev => prev.map(g => g.id === goal.id ? { ...g, status: next } : g));
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingGoal(null);
    setForm({ title: '', description: '', target_date: '', status: 'active' });
  };

  const handleToggleShare = async (goal: Goal) => {
    const newValue = !goal.shared_with_coach;
    await toggleGoalSharedWithCoach(goal.id, newValue);
    setGoals(prev => prev.map(g => g.id === goal.id ? { ...g, shared_with_coach: newValue } : g));
  };

  const handleAddMilestone = (goal: Goal) => {
    setMilestoneFormGoalId(goal.id);
    setMilestoneFormTitle('');
  };

  const handleMilestoneCancel = () => {
    setMilestoneFormGoalId(null);
    setMilestoneFormTitle('');
  };

  const handleMilestoneSubmit = async (e: React.FormEvent, goal: Goal) => {
    e.preventDefault();
    if (!user || !milestoneFormTitle.trim()) return;
    const created = await createMilestone(user.id, {
      title: milestoneFormTitle,
      achieved_at: new Date().toISOString(),
      goal_id: goal.id,
    });
    if (created) {
      setMilestones(prev => [created, ...prev]);
    }
    setMilestoneFormGoalId(null);
    setMilestoneFormTitle('');
  };

  const handleDeleteMilestone = async (id: string) => {
    await deleteMilestone(id);
    setMilestones(prev => prev.filter(m => m.id !== id));
  };

  if (isLoading) return <LoadingSpinner message="Loading goals..." />;

  const activeGoals = goals.filter(g => g.status === 'active');
  const pausedGoals = goals.filter(g => g.status === 'paused');
  const completedGoals = goals.filter(g => g.status === 'completed');
  const abandonedGoals = goals.filter(g => g.status === 'abandoned');

  const renderGoalRow = (goal: Goal) => (
    <GoalRow
      key={goal.id}
      goal={goal}
      milestones={milestones}
      onStatusCycle={handleStatusCycle}
      onEdit={handleEdit}
      onDelete={handleDelete}
      onToggleShare={handleToggleShare}
      onAddMilestone={handleAddMilestone}
      onDeleteMilestone={handleDeleteMilestone}
      milestoneFormGoalId={milestoneFormGoalId}
      milestoneFormTitle={milestoneFormTitle}
      onMilestoneTitleChange={setMilestoneFormTitle}
      onMilestoneSubmit={handleMilestoneSubmit}
      onMilestoneCancel={handleMilestoneCancel}
    />
  );

  return (
    <div className="p-6 md:p-8 max-w-3xl">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-sage-100 rounded-xl flex items-center justify-center">
            <Target className="w-5 h-5 text-sage-600" />
          </div>
          <div>
            <h1 className="freud-heading-xl text-chocolate-900">Goals</h1>
            <p className="text-sm text-chocolate-500">
              {activeGoals.length} active
              {completedGoals.length > 0 && <span className="text-blue-600"> · {completedGoals.length} completed</span>}
              {milestones.length > 0 && <span className="text-yellow-600"> · {milestones.length} milestones</span>}
            </p>
          </div>
        </div>
        <button onClick={() => { setEditingGoal(null); setShowForm(true); }} className="freud-btn-primary flex items-center gap-2">
          <Plus className="w-4 h-4" />
          New Goal
        </button>
      </div>

      <p className="text-xs text-chocolate-400 mb-6">
        Set as many goals as you need. Add milestones to track your progress, and celebrate completed achievements to stay motivated.
      </p>

      {showForm && (
        <div className="freud-card-elevated p-6 mb-6">
          <h2 className="freud-heading-lg text-chocolate-900 mb-4">{editingGoal ? 'Edit Goal' : 'New Goal'}</h2>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="freud-label">Title *</label>
              <input className="freud-input" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} placeholder="What do you want to achieve?" required />
            </div>
            <div>
              <label className="freud-label">Description</label>
              <textarea className="freud-input" rows={2} value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} placeholder="Why does this matter?" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="freud-label">Target Date</label>
                <input type="date" className="freud-input" value={form.target_date} onChange={e => setForm(p => ({ ...p, target_date: e.target.value }))} />
              </div>
              <div>
                <label className="freud-label">Status</label>
                <select className="freud-input" value={form.status} onChange={e => setForm(p => ({ ...p, status: e.target.value as Goal['status'] }))}>
                  {(Object.keys(STATUS_LABELS) as Goal['status'][]).map(s => (
                    <option key={s} value={s}>{STATUS_LABELS[s]}</option>
                  ))}
                </select>
              </div>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="submit" className="freud-btn-primary">{editingGoal ? 'Save Changes' : 'Create Goal'}</button>
              <button type="button" onClick={handleCancel} className="freud-btn-secondary">Cancel</button>
            </div>
          </form>
        </div>
      )}

      {goals.length === 0 ? (
        <div className="freud-card-elevated p-12 text-center">
          <Target className="w-12 h-12 text-cream-400 mx-auto mb-3" />
          <p className="text-chocolate-500">No goals yet. Set your first goal to get started.</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Active Goals */}
          {activeGoals.length > 0 && (
            <CollapsibleSection
              title="Active Goals"
              count={activeGoals.length}
              icon={Target}
              iconBg="bg-sage-100"
              iconColor="text-sage-600"
              defaultOpen={true}
            >
              {activeGoals.map(renderGoalRow)}
            </CollapsibleSection>
          )}

          {/* Paused Goals */}
          {pausedGoals.length > 0 && (
            <CollapsibleSection
              title="Paused"
              count={pausedGoals.length}
              icon={Clock}
              iconBg="bg-amber-100"
              iconColor="text-amber-600"
              defaultOpen={false}
            >
              {pausedGoals.map(renderGoalRow)}
            </CollapsibleSection>
          )}

          {/* Completed Achievements */}
          {completedGoals.length > 0 && (
            <CollapsibleSection
              title="Completed Achievements"
              count={completedGoals.length}
              icon={Trophy}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              defaultOpen={true}
            >
              {completedGoals.map(renderGoalRow)}
            </CollapsibleSection>
          )}

          {/* Abandoned (collapsed) */}
          {abandonedGoals.length > 0 && (
            <CollapsibleSection
              title="Abandoned"
              count={abandonedGoals.length}
              icon={X}
              iconBg="bg-red-50"
              iconColor="text-red-400"
              defaultOpen={false}
            >
              {abandonedGoals.map(renderGoalRow)}
            </CollapsibleSection>
          )}
        </div>
      )}
    </div>
  );
}
